import {
  Component,
  computed,
  effect,
  Inject,
  output,
  untracked,
} from '@angular/core';
import {
  OutlookIqBoostStore,
  OutlookIqBoostService,
  canRetryAiError,
  classifyAiError,
  getAiErrorTranslationKey,
} from '@maximizer/outlook/ai/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';
import { catchError, finalize, of, take, tap } from 'rxjs';
import { OutlookIqBoostLoaderComponent } from '../loaders/loader.component';
import { MarkdownRenderingPipe, SafeHtmlPipe } from '@maximizer/ai/ui';
import { OutlookDeAnonymizerPipe } from '../../pipes/de-anonymizer.pipe';
import { OutlookCreateCRMObjectPipe } from '../../pipes/create-crm-object.pipe';
import { OutlookClickableRefPipe } from '../../pipes/clickable-ref.pipe';
import { OutlookEntityKeyRefPipe } from '../../pipes/entity-key-ref.pipe';
import { sanitizeMarkdownToHtml } from '@maximizer/shared/util';
import { CDN_URL } from '@maximizer/core/shared/domain';
import {
  EntryStore,
  TrackerService,
} from '@maximizer/outlook/shared/data-access';
import {
  EventAction,
  EventJourney,
  EventModule,
} from '@maximizer/outlook/shared/domain';

@Component({
  selector: 'maximizer-outlook-iq-boost-summary',
  templateUrl: './summary.component.html',
  styles: ':host { display: flex; flex-direction: column; min-height: 0; }',
  imports: [
    KENDO_BUTTON,
    KENDO_TOOLTIP,
    TranslatePipe,
    OutlookIqBoostLoaderComponent,
    MarkdownRenderingPipe,
    SafeHtmlPipe,
    OutlookDeAnonymizerPipe,
    OutlookCreateCRMObjectPipe,
    OutlookClickableRefPipe,
    OutlookEntityKeyRefPipe,
  ],
})
export class OutlookIqBoostSummaryComponent {
  openLibrary = output<void>();

  readonly prompt = computed(() => this.iqBoostStore.currentPrompt());
  readonly aiResponse = computed(() => this.iqBoostStore.summaryResponse());
  readonly isLoading = computed(() => this.iqBoostStore.loading());
  readonly error = computed(() => this.iqBoostStore.error());
  readonly errorMessage = computed(() => {
    const errorType = this.error();
    return errorType ? getAiErrorTranslationKey(errorType) : null;
  });
  readonly canRetry = computed(() => {
    const errorType = this.error();
    return errorType ? canRetryAiError(errorType) : false;
  });

  constructor(
    @Inject(CDN_URL) readonly cdn: string,
    private readonly iqBoostService: OutlookIqBoostService,
    private readonly iqBoostStore: OutlookIqBoostStore,
    private readonly tracker: TrackerService,
    private readonly entryStore: EntryStore,
  ) {
    effect(() => {
      const promptSelection = this.iqBoostStore.currentPromptSelection();
      if (!promptSelection) return;

      if (this.iqBoostStore.summaryResponse()) return;

      this.iqBoostStore.setLoading(true);

      const category = promptSelection.isEmailAssist ? 'outlook' : 'core';

      untracked(() => {
        this.iqBoostService
          .generateResponse(promptSelection)
          .pipe(
            take(1),
            tap((text) => {
              this.iqBoostStore.setSummaryResponse(
                sanitizeMarkdownToHtml(text),
              );
              this.tracker.trackEvent({
                module: EventModule.IqBoost,
                journey: EventJourney.Summary,
                action: EventAction.Completed,
                metadata: { entry: this.entryStore.type(), category },
              });
            }),
            catchError((err: unknown) => {
              this.iqBoostStore.setError(classifyAiError(err));
              return of(null);
            }),
            finalize(() => this.iqBoostStore.setLoading(false)),
          )
          .subscribe();
      });
    });
  }

  retry(): void {
    const promptSelection = this.iqBoostStore.currentPromptSelection();
    if (!promptSelection) return;
    this.iqBoostStore.clearError();
    this.iqBoostStore.setLoading(true);
    this.iqBoostService
      .generateResponse(promptSelection, true)
      .pipe(
        take(1),
        tap((text) =>
          this.iqBoostStore.setSummaryResponse(sanitizeMarkdownToHtml(text)),
        ),
        catchError((err: unknown) => {
          this.iqBoostStore.setError(classifyAiError(err));
          return of(null);
        }),
        finalize(() => this.iqBoostStore.setLoading(false)),
      )
      .subscribe();
  }
}
